import React, { useCallback, useEffect, useState } from "react";
import { Share, Trash2 } from "lucide-react";
import { api, shareText } from "@/lib/api";
import { isManagerRole, useAuth } from "@/lib/auth";
import { formatLastSeen } from "@/lib/presence";
import { useToast } from "@/lib/toast";
import { Avatar, Button, Card, Input } from "@/components/app/Primitives";
import { PhoneInput } from "@/components/app/PhoneInput";
import { StackPage, WhatsAppIcon } from "@/components/app/Layout";
import { CenterSpinner } from "@/components/app/Spinner";

export default function Team() {
  const { activeWorkspace, user } = useAuth();
  const toast = useToast();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invitePhone, setInvitePhone] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [busy, setBusy] = useState(false);
  const isManager = isManagerRole(activeWorkspace?.role);

  const load = useCallback(async () => {
    if (!activeWorkspace) return;
    setLoading(true);
    try { setMembers(await api(`/workspaces/${activeWorkspace.id}/members?include_pending=true`)); }
    catch (e) { toast.show(e instanceof Error ? e.message : "Failed to load", "error"); }
    finally { setLoading(false); }
  }, [activeWorkspace, toast]);
  useEffect(() => { load(); }, [load]);

  const generateInvite = async () => {
    try {
      return await api(`/workspaces/${activeWorkspace.id}/whatsapp-invite`, { method: "POST", body: { phone_e164: invitePhone.trim() || undefined, name: inviteName.trim() || undefined, role: "employee" } });
    } catch (e) { toast.show(e instanceof Error ? e.message : "Failed to build invite", "error"); return null; }
  };
  const viaWhatsApp = async () => {
    if (!activeWorkspace) return;
    setBusy(true);
    try {
      const r = await generateInvite();
      if (!r) return;
      window.open(r.wa_link_number || r.wa_link_generic, "_blank", "noopener");
      toast.show("WhatsApp opened with your invite", "success");
      setInvitePhone(""); setInviteName("");
    } finally { setBusy(false); }
  };
  const viaShare = async () => {
    if (!activeWorkspace) return;
    setBusy(true);
    try {
      const r = await generateInvite();
      if (!r) return;
      const res = await shareText({ message: r.message, url: r.join_url });
      if (res === "copied") toast.show("Invite copied to clipboard", "success");
      setInvitePhone(""); setInviteName("");
    } finally { setBusy(false); }
  };
  const approve = async (m) => {
    try { await api(`/members/${m.id}/approve`, { method: "POST", body: { role: "employee" } }); toast.show(`${m.full_name} approved`, "success"); load(); }
    catch (e) { toast.show(e instanceof Error ? e.message : "Failed", "error"); }
  };
  const remove = async (m) => {
    try { await api(`/members/${m.id}/reject`, { method: "POST" }); toast.show("Removed", "info"); load(); }
    catch (e) { toast.show(e instanceof Error ? e.message : "Failed", "error"); }
  };
  const statusTone = (s) => (s === "pending_manager_approval" ? "warning" : "primary");
  const statusLabel = (s) => (s === "pending_manager_approval" ? "Pending approval" : "Pending accept");

  return (
    <StackPage title="Team" subtitle={`${activeWorkspace?.name || ""} · ${members.length} members`} testID="team-page" contentClassName="p-4 space-y-2.5">
      {isManager ? (
        <Card className="mb-1" testID="team-invite-card">
          <div className="flex items-center gap-2"><WhatsAppIcon size={18} /><p className="text-[15px] font-extrabold text-a-text">Invite via WhatsApp</p></div>
          <p className="mt-1 text-[13px] text-a-muted">Add a phone number to open a pre-filled WhatsApp message, or use Share to pick from any messenger.</p>
          <div className="mt-3 space-y-2.5">
            <PhoneInput testID="team-invite-phone" label="Invitee phone (optional)" placeholder="98765 43210" valueE164={invitePhone} onChangeE164={(e) => setInvitePhone(e)} />
            <Input testID="team-invite-name" label="Their name (optional)" placeholder="e.g. Anita Sharma" value={inviteName} onChangeText={setInviteName} />
          </div>
          <div className="mt-3 flex gap-2">
            <div className="flex-[2]"><Button testID="team-invite-whatsapp" label={busy ? "…" : "Send via WhatsApp"} onPress={viaWhatsApp} loading={busy} icon={<WhatsAppIcon size={16} color="#fff" />} /></div>
            <div className="flex-1"><Button testID="team-invite-share" label="Share" variant="ghost" onPress={viaShare} loading={busy} icon={<Share size={16} />} /></div>
          </div>
          <p className="mt-2.5 text-[11px] text-a-muted">Or share your workspace code from the Workspaces screen so teammates can request access.</p>
        </Card>
      ) : null}
      {loading && members.length === 0 ? <CenterSpinner /> : members.map((m) => {
        const pending = m.status && m.status !== "active";
        const isSelf = user?.id === m.user_id;
        return (
          <Card key={m.id} testID={`member-${m.id}`}>
            <div className="flex items-center gap-3">
              <Avatar src={m.avatar_base64} name={m.full_name} size={44} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold text-a-text">{m.full_name}{isSelf ? " (you)" : ""}</p>
                <p className="truncate text-xs text-a-muted">{(m.designation || m.role) + " · " + m.email}</p>
                <div className="mt-1 flex items-center gap-1.5">
                  {pending ? (
                    <><span className={`h-2 w-2 rounded-full ${statusTone(m.status) === "warning" ? "bg-a-warning" : "bg-a-primary"}`} /><span className={`text-[11px] font-bold ${statusTone(m.status) === "warning" ? "text-a-warning" : "text-a-primary"}`}>{statusLabel(m.status)}</span></>
                  ) : (
                    <><span className={`h-2 w-2 rounded-full ${m.online ? "bg-a-success" : "bg-a-muted"}`} /><span className={`text-[11px] font-semibold ${m.online ? "text-a-success" : "text-a-muted"}`}>{m.online ? "Active now" : m.hide_last_seen && !isSelf ? "Offline" : m.last_active_at ? `Last seen ${formatLastSeen(m.last_active_at)}` : "Offline"}</span></>
                  )}
                </div>
              </div>
              {isManager && m.status === "pending_manager_approval" ? <button onClick={() => approve(m)} data-testid={`member-approve-${m.id}`} className="rounded-lg bg-a-success px-3 py-1.5 text-xs font-bold text-white">Approve</button> : null}
              {isManager && !isSelf && m.role !== "owner" ? <button onClick={() => remove(m)} data-testid={`member-remove-${m.id}`} className="text-a-danger hover:opacity-75"><Trash2 size={18} /></button> : null}
            </div>
          </Card>
        );
      })}
    </StackPage>
  );
}
